import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Select from "react-select";
import ApiService from "../util/ApiService";
import Swal from "sweetalert2";
import LoadingScreen from "../util/LoadingScreen";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CustomPagination from "../util/CustomPagination";
import DropdownPortal from "../util/DropdownPortal";
import VideoCallDetailModal from "./VideoCallDetailModal";
import VideoCreateAttendanceModal from "./VideoCreateAttendanceModal";

require("./assets/css/Video.css");

const VideoCallsPage = () => {
    document.title = "Atendimentos por Vídeo | Plantão Rápido";

    const navigate = useNavigate();
    const buttonRefs = useRef({});
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [loadingPage, setLoadingPage] = useState(false);
    const [attendances, setAttendances] = useState([]);
    const [selectedAttendance, setSelectedAttendance] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [departments, setDepartments] = useState([]);

    const [filters, setFilters] = useState({
        status: null,
        department_id: null
    });

    const statusOptions = [
        { value: "waiting", label: "Aguardando" },
        { value: "in_queue", label: "Na Fila" },
        { value: "in_progress", label: "Em Atendimento" },
        { value: "completed", label: "Finalizado" },
        { value: "cancelled", label: "Cancelado" },
        { value: "no_show", label: "Não Compareceu" }
    ];

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        lastPage: 0
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchAttendances();
        fetchDepartments();

        // Auto-refresh every 10 seconds
        const interval = setInterval(() => {
            fetchAttendances(pagination.current, true);
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    const fetchDepartments = async () => {
        try {
            // Using video departments endpoint
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/departments/list-department`,
                { per_page: 100, status: 1 },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setDepartments(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Erro ao carregar departamentos:', error);
        }
    };

    const fetchAttendances = async (page = 1, silent = false) => {
        try {
            if (!silent) setLoading(true);

            const params = {
                page: page,
                per_page: pagination.pageSize
            };

            if (filters?.status?.value) {
                params.status = filters.status.value;
            }
            if (filters?.department_id?.value) {
                params.department_id = filters.department_id.value;
            }

            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/attendances`,
                params,
                true
            );

            const { data, pagination: pagInfo } = ApiService.handleApiResponse(response);
            setAttendances(data || []);
            setPagination(pagInfo || { current: 1, pageSize: 10, total: 0 });
        } catch (error) {
            if (!silent) ApiService.handleApiError(error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        fetchAttendances(page);
    };

    const handleClearFilters = () => {
        setFilters({
            status: null,
            department_id: null
        });
    };

    const handleStartAttendance = async (attendance) => {
        try {
            setLoadingPage(true);

            const attendanceId = attendance.attendance_id || attendance.id;

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/attendances/${attendanceId}/start`,
                {},
                true
            );

            if (response.code === 200) {
                navigate(`/admin/video/sala/${attendanceId}?host=true`);
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoadingPage(false);
        }
    };

    const handleCompleteAttendance = async (attendance) => {
        const { value: notes } = await Swal.fire({
            title: 'Finalizar atendimento',
            input: 'textarea',
            inputLabel: 'Observações (opcional)',
            inputPlaceholder: 'Digite suas observações...',
            showCancelButton: true,
            confirmButtonText: 'Finalizar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#28a745'
        });

        if (notes === undefined) return;

        try {
            setLoadingPage(true);

            const attendanceId = attendance.attendance_id || attendance.id;

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/attendances/${attendanceId}/complete`,
                { notes },
                true
            );

            if (response.code === 200) {
                Swal.fire('Sucesso', 'Atendimento finalizado!', 'success');
                fetchAttendances(pagination.current);
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoadingPage(false);
        }
    };

    const handleCancelAttendance = async (attendance) => {
        const result = await Swal.fire({
            title: 'Cancelar atendimento?',
            text: 'Esta ação não pode ser desfeita.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, cancelar',
            cancelButtonText: 'Não'
        });

        if (!result.isConfirmed) return;

        try {
            setLoadingPage(true);

            const attendanceId = attendance.attendance_id || attendance.id;

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/attendances/${attendanceId}/cancel`,
                {},
                true
            );

            if (response.code === 200) {
                Swal.fire('Cancelado', 'Atendimento cancelado.', 'info');
                fetchAttendances(pagination.current);
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoadingPage(false);
        }
    };

    const handleViewDetail = (attendance) => {
        setSelectedAttendance(attendance);
        setShowDetailModal(true);
    };

    const handleResendInvite = async (attendance) => {
        const result = await Swal.fire({
            title: 'Reenviar convite?',
            text: `O convite será enviado para ${attendance.client?.email || 'o email do cliente'}.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, enviar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            setLoadingPage(true);

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/attendances/${attendance.attendance_id}/resend-invite`,
                {},
                true
            );

            if (response.code === 200) {
                Swal.fire({
                    title: 'Convite Enviado!',
                    html: `<p>O convite foi reenviado para <strong>${response.data?.email_sent_to || attendance.client?.email}</strong></p>`,
                    icon: 'success',
                    confirmButtonText: 'Ok'
                });
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoadingPage(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            waiting: { class: 'bg-secondary', icon: 'fa-hourglass', text: 'Aguardando' },
            in_queue: { class: 'bg-warning', icon: 'fa-clock', text: 'Na Fila' },
            in_progress: { class: 'bg-success', icon: 'fa-video-camera', text: 'Em Atendimento' },
            completed: { class: 'bg-info', icon: 'fa-check', text: 'Finalizado' },
            cancelled: { class: 'bg-danger', icon: 'fa-times', text: 'Cancelado' },
            no_show: { class: 'bg-dark', icon: 'fa-user-times', text: 'Não Compareceu' }
        };

        const badge = badges[status] || badges.waiting;

        return (
            <span className={`badge ${badge.class} d-inline-flex align-items-center`}>
                <i className={`fa ${badge.icon} me-1`}></i>
                {badge.text}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getQueueCount = () => {
        return attendances.filter(a => a.status === 'in_queue').length;
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Atendimentos" pageTitle="Vídeo Chamada" />

                    {loadingPage && <LoadingScreen />}

                    {/* Stats Cards */}
                    <Row className="mb-4">
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-warning">
                                    {attendances.filter(a => a.status === 'in_queue').length}
                                </div>
                                <div className="stat-label">Na Fila</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-success">
                                    {attendances.filter(a => a.status === 'in_progress').length}
                                </div>
                                <div className="stat-label">Em Atendimento</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-info">
                                    {attendances.filter(a => a.status === 'completed').length}
                                </div>
                                <div className="stat-label">Finalizados</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value">{pagination.total}</div>
                                <div className="stat-label">Total</div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filters */}
                    <Row className="mb-3">
                        <Col md={4}>
                            <div className="form-group">
                                <label>Status</label>
                                <Select
                                    options={statusOptions}
                                    value={filters.status}
                                    onChange={(option) => setFilters(prev => ({ ...prev, status: option }))}
                                    placeholder="Selecione o status"
                                    isClearable
                                />
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="form-group">
                                <label>Departamento</label>
                                <Select
                                    options={departments.map(d => ({ value: d.id, label: d.name }))}
                                    value={filters.department_id}
                                    onChange={(option) => setFilters(prev => ({ ...prev, department_id: option }))}
                                    placeholder="Selecione o departamento"
                                    isClearable
                                />
                            </div>
                        </Col>
                        <Col md={4} className="d-flex align-items-end justify-content-end">
                            <button
                                className="btn btn-outline-secondary me-2"
                                onClick={handleClearFilters}
                            >
                                <i className="fa fa-eraser me-1"></i> Limpar
                            </button>
                            <button
                                className="btn btn-outline-primary me-2"
                                onClick={() => fetchAttendances(1)}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Buscando...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-search me-1"></i> Buscar
                                    </>
                                )}
                            </button>
                            <button
                                className="btn btn-success"
                                onClick={() => setShowCreateModal(true)}
                            >
                                <i className="fa fa-plus me-1"></i> Novo Atendimento
                            </button>
                        </Col>
                    </Row>

                    {/* Queue alert */}
                    {getQueueCount() > 0 && (
                        <div className="alert alert-warning d-flex align-items-center mb-3">
                            <i className="fa fa-exclamation-triangle me-2 fa-lg"></i>
                            <div>
                                <strong>{getQueueCount()} cliente(s)</strong> aguardando atendimento!
                            </div>
                        </div>
                    )}

                    {/* Attendances List */}
                    <div className="list-container">
                        {loading ? (
                            <Skeleton count={5} height={100} />
                        ) : attendances.length > 0 ? (
                            attendances.map((attendance) => (
                                <Col lg={12} key={attendance.id}>
                                    <Card className={`mb-2 p-2 shadow-sm ${attendance.status === 'in_queue' ? 'border-warning border-2' : ''}`}>
                                        <CardBody className="d-flex justify-content-between align-items-center p-3">
                                            <div className="d-flex align-items-center">
                                                <div className="video-user-avatar me-3">
                                                    {attendance.client?.name?.charAt(0)?.toUpperCase() || 'C'}
                                                </div>
                                                <div>
                                                    <h5 className="mb-1">{attendance.client?.name || 'Cliente'}</h5>
                                                    <p className="text-muted mb-1">
                                                        <i className="fa fa-building me-1"></i>
                                                        {attendance.department?.name || 'Sem departamento'}
                                                    </p>
                                                    <p className="text-muted mb-0 small">
                                                        <i className="fa fa-ticket me-1"></i>
                                                        {attendance.protocol}
                                                        <span className="ms-3">
                                                            <i className="fa fa-clock me-1"></i>
                                                            {formatDate(attendance.created_at)}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="text-center">
                                                {getStatusBadge(attendance.status)}
                                                {attendance.professional && (
                                                    <p className="text-muted mb-0 mt-2 small">
                                                        <i className="fa fa-user me-1"></i>
                                                        {attendance.professional.name}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    ref={(el) => (buttonRefs.current[attendance.id] = el)}
                                                    onClick={() => setDropdownOpenId(dropdownOpenId === attendance.id ? null : attendance.id)}
                                                >
                                                    <i className="fa fa-ellipsis-v"></i> Ações
                                                </button>

                                                <DropdownPortal
                                                    parentRef={{ current: buttonRefs.current[attendance.id] }}
                                                    show={dropdownOpenId === attendance.id}
                                                    onClose={() => setDropdownOpenId(null)}
                                                >
                                                    <ul className="dropdown-menu show">
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    handleViewDetail(attendance);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                <i className="fa fa-eye me-2"></i> Ver Detalhes
                                                            </button>
                                                        </li>
                                                        {attendance.status === 'in_queue' && (
                                                            <li>
                                                                <button
                                                                    className="dropdown-item text-success"
                                                                    onClick={() => {
                                                                        handleStartAttendance(attendance);
                                                                        setDropdownOpenId(null);
                                                                    }}
                                                                >
                                                                    <i className="fa fa-video-camera me-2"></i> Atender
                                                                </button>
                                                            </li>
                                                        )}
                                                        {attendance.status === 'in_progress' && (
                                                            <>
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item text-primary"
                                                                        onClick={() => {
                                                                            navigate(`/admin/video/sala/${attendance.attendance_id || attendance.id}?host=true`);
                                                                            setDropdownOpenId(null);
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-sign-in me-2"></i> Entrar na Sala
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item text-success"
                                                                        onClick={() => {
                                                                            handleCompleteAttendance(attendance);
                                                                            setDropdownOpenId(null);
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-check me-2"></i> Finalizar
                                                                    </button>
                                                                </li>
                                                            </>
                                                        )}
                                                        {['waiting', 'in_queue'].includes(attendance.status) && (
                                                            <>
                                                                {attendance.client?.email && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item text-info"
                                                                            onClick={() => {
                                                                                handleResendInvite(attendance);
                                                                                setDropdownOpenId(null);
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-envelope me-2"></i> Reenviar Convite
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item text-danger"
                                                                        onClick={() => {
                                                                            handleCancelAttendance(attendance);
                                                                            setDropdownOpenId(null);
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-times me-2"></i> Cancelar
                                                                    </button>
                                                                </li>
                                                            </>
                                                        )}
                                                    </ul>
                                                </DropdownPortal>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))
                        ) : (
                            <div className="video-empty-state">
                                <i className="fa fa-video-camera"></i>
                                <h5 className="text-muted">Nenhum atendimento encontrado</h5>
                                <p className="text-muted">Os atendimentos por vídeo aparecerão aqui.</p>
                            </div>
                        )}
                    </div>

                    <CustomPagination
                        onChange={handlePageChange}
                        pagination={pagination}
                    />

                    {showDetailModal && (
                        <VideoCallDetailModal
                            show={showDetailModal}
                            onHide={() => {
                                setShowDetailModal(false);
                                setSelectedAttendance(null);
                            }}
                            call={selectedAttendance}
                        />
                    )}

                    {showCreateModal && (
                        <VideoCreateAttendanceModal
                            show={showCreateModal}
                            onHide={() => setShowCreateModal(false)}
                            onSuccess={() => {
                                fetchAttendances(1);
                            }}
                        />
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default VideoCallsPage;
